import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { usePostInfo } from '../../../../../utils/PostInfoProvider';
import uploadPDF from '../../../../../api/pdf/uploadPDF';
import processPDF from '../../../../../api/pdf/processPDF';
import deletePDF from '../../../../../api/pdf/deletePDF';
import extractPDFText from '../../../../../api/pdf/extractPDFText';
import formatPageData, { formatSlug } from './formatPageData';
import postAddPost from '../../../../../api/graphql/mutations/postAddPost';
import postDeletePost from '../../../../../api/graphql/mutations/postDeletePost';

// NOTE: initial structure of the component
const initialState = {
  type: 'PdfMagazineGallery',
  edition: '',
  year: '',
  month: '',
  pages: 0,
  subtitle: {
    en: '',
    es: '',
    ca: '',
    pt: '',
  },
  pdf: {
    key: '',
    file: '',
  },
};

const AnimatedDots = () => {
  const [dots, setDots] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);
  return <span style={{ display: 'inline-block', width: '1.5em', textAlign: 'left' }}>{dots}</span>;
};

const buttonStatus = {
  ready: { message: 'Upload magazine', styles: 'bg-blue' },
  success: { message: 'Upload magazine', styles: 'bg-blue' },
  uploadError: { message: 'Upload magazine', styles: 'bg-blue' },
  deleteError: { message: 'Upload magazine', styles: 'bg-blue' },
  uploading: { message: 'Uploading', styles: 'bg-green', animated: true },
  processing: { message: 'Processing PDF pages', styles: 'bg-green', animated: true },
  extractingText: { message: 'Extracting text', styles: 'bg-green', animated: true },
  creatingPosts: { message: 'Creating posts for pages', styles: 'bg-green', animated: true },
};

const PdfMagazineGallery = ({ components, handlerSetComponents, id, activeLanguage }) => {
  const [state, setState] = useState(initialState); // NOTE: this is the component you are working at
  const [selectedFile, setSelectedFile] = useState('');
  const [pdfStatus, setPdfStatus] = useState('');
  const [displayDelete, setDisplayDelete] = useState(false);

  const { postData } = usePostInfo(); // NOTE: in case you need data from the main post

  // NOTE: component index inside the Drag and Drop
  const componentIndex = components.findIndex(obj => obj.id === id);
  const { component } = components[componentIndex];

  useEffect(() => {
    if (component) setState(component);
  }, []);

  const handleSubtitleSave = (e) => {
    const { value } = e.target;

    const newState = {
      ...state,
      subtitle: {
        ...state.subtitle,
        [activeLanguage]: value,
      },
    };

    setState(newState);
  };

  const handleFileChange = (e) => {
    e.preventDefault();
    if (e.target.files.length) {
      setSelectedFile(e.target.files[0].name);
      setPdfStatus('ready');
    } else {
      setPdfStatus('');
      setSelectedFile('');
    }
  };

  // NOTE: to push component to the main post container you shoud call handlerSetComponents(state, index);
  const deletePagePosts = (year, edition, pages) => Promise.all(new Array(pages).fill(0).map((el, index) => {
    const slugName = formatSlug(year, edition, index + 1);
    return postDeletePost({ id: '', slugName });
  }));

  const handleUpload = async (e) => {
    e.preventDefault();

    const formFile = e.target.querySelector('#magazineFile').files[0];
    const year = e.target.querySelector('#year').value;
    const edition = e.target.querySelector('#edition').value;
    const month = e.target.querySelector('#month').value;

    if (!formFile) {
      setPdfStatus('noFile');
      return;
    }

    let key = null;

    try {
      setPdfStatus('uploading');
      key = await uploadPDF(formFile);
      if (!key) throw new Error('Could not upload the PDF');

      setPdfStatus('processing');
      const pageNumData = await processPDF(key);
      // Stop here. Carrying on used to create the page posts for a PDF that was
      // then deleted, and left them behind (RDL 400, PRI-486).
      if (!pageNumData || !pageNumData.numberOfPages) throw new Error('Could not split the PDF into pages');

      setPdfStatus('extractingText');
      const pagesData = await extractPDFText(key);
      if (!pagesData || !pagesData.texts) throw new Error('Could not extract the PDF text');

      setPdfStatus('creatingPosts');
      const pages = Math.max(pageNumData.numberOfPages, pagesData.texts.length);

      // postAddPost refuses a slug that already exists, so page posts left by an
      // earlier upload of this issue would silently survive. Replace them.
      await deletePagePosts(year, edition, pages);

      const added = await Promise.all(pagesData.texts.map((pageText, index) => {
        const formattedData = formatPageData(postData, pageText, month, year, edition, index + 1);
        return postAddPost(formattedData);
      }));

      if (added.some(result => !result || !result.isAdded)) {
        await deletePagePosts(year, edition, pages);
        throw new Error('Could not create the page posts');
      }

      const newState = {
        ...state,
        edition,
        year,
        month,
        pages: pageNumData.numberOfPages,
        pdf: {
          key,
          file: formFile.name,
        },
      };

      setState(newState);
      setPdfStatus('success');
      setDisplayDelete(false);
    } catch (error) {
      console.error(error);
      // Don't leave the uploaded PDF and its page files in S3
      if (key) deletePDF(key).catch(err => console.error(err));
      setPdfStatus('uploadError');
    }
  };

  const handleDelete = async () => {
    if (state.pdf.key) {
      setPdfStatus('deleting');
      await deletePDF(state.pdf.key);
      Promise.all(new Array(state.pages).fill(0).map((el, index) => {
        const slugName = formatSlug(state.year, state.edition, index + 1);
        return postDeletePost({ id: '', slugName });
      })).then(() => {
        const newState = {
          ...state,
          edition: '',
          year: '',
          pages: 0,
          pdf: {
            key: '',
            file: '',
          },
        };

        setState(newState);

        document.querySelector('#magazineFile').value = [];
        setSelectedFile('');
        setPdfStatus('');
      }).catch((error) => {
        console.log(error);
        setPdfStatus('deleteError');
      });
    }
  };

  // NOTE: update the state of the main post
  useEffect(() => {
    const updatedComponent = {
      id,
      component: state,
    };

    handlerSetComponents(updatedComponent, componentIndex);
  }, [state]);

  return (
    <div className="text-black text-center flex flex-col items-center">
      <form className="text-left m-5">
        <label
          className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
          htmlFor="pdf-subtitle">
          Subtitle
        </label>
        <input
          className="appearance-none block
          bg-gray-200 text-gray-700 border border-red-500
          rounded py-3 px-4 w-full leading-tight focus:outline-none
          focus:bg-white"
          id="pdf-subtitle"
          type="text"
          size="50"
          placeholder="Enero 2010. RDL 280"
          onChange={handleSubtitleSave}
          value={state.subtitle && state.subtitle[activeLanguage]}
        />
      </form>
      {state.pdf && state.pdf.file && (
        displayDelete ? (
          <div className="flex flex-col p-5 mb-5 justify-center items-center bg-red-lightest rounded">
            {pdfStatus !== 'deleting' ? (
              <>
                <span className="font-americaBlack text-black mb-2">{`Are you sure you want to unlink and delete ${state.pdf.file}?`}</span>
                <div>
                  <button type="button" onClick={handleDelete} className="font-americaMonoBold text-black bg-red border-1 border-black text-xs px-6 py-2 rounded-full uppercase mr-1">
                    Yes
                  </button>
                  <button type="button" onClick={() => setDisplayDelete(false)} className="font-americaMonoBold text-black border-1 border-grey text-xs px-6 py-2 rounded-full uppercase">
                    No
                  </button>
                </div>
              </>
            ) : (
              <span className="font-americaBlack text-black">{`Deleting ${state.pdf.file}`}</span>
            )}
          </div>
        ) : (
          <div className="p-4 m-5 border-1 rounded-lg border-grey">
            <div className="text-md font-americaBlack text-black mb-2">
              {`Linked PDF: ${state.pdf.file}`}
              <button type="button" onClick={() => setDisplayDelete(true)} className="bg-red font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase text-white ml-4">
                Delete PDF
              </button>
            </div>
            <p className="text-grey-darker text-xs font-americaMonoBold">Do not remove this module without deleting the PDF first.</p>
          </div>
        ))
      }
      <form className={`${state.pdf.file ? 'opacity-50' : ''} flex flex-col justify-center items-center w-max`} id="fileForm" name="fileForm" onSubmit={handleUpload}>
        <div className="mx-3">
          <label htmlFor="magazineFile" className="font-americaMonoBold inline-flex px-6 py-2 rounded-full uppercase bg-black text-white flex flex-row items-center cursor-pointer m-3">
            Select PDF
            <span className="font-icon icon-add-button text-base text-white font-bold pl-2" />
          </label>
          <input type="file" id="magazineFile" name="magazineFile" disabled={!!state.pdf.file} accept=".pdf" onChange={handleFileChange} className="opacity-0 w-0 h-0 absolute top-0" required />
          <span className="text-xs font-americaBlack text-black">{selectedFile ? `Selected file: ${selectedFile}` : 'No file selected'}</span>
        </div>  
        <div className="w-full md:w-full mx-3 flex text-left">
          <div className="m-1 flex-grow inline-block">
            <label htmlFor="month" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Month</label>
            <input type="text" id="month" name="month" placeholder="Enero" disabled={!!state.pdf.file} className="appearance-none block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" required />
          </div>
          <div className="m-1 flex-grow inline-block">
            <label htmlFor="year" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Year</label>
            <input list="year-list" id="year" name="year" placeholder="2010" disabled={!!state.pdf.file} size="8" className="appearance-none uppercase block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" required />
            <datalist id="year-list">{ Array(11).fill(0).map((el, i) => <option key={`year-${200 - i}`} value={2020 - i} />) }</datalist>
          </div>
          <div className="m-1 flex-grow inline-block">
            <label htmlFor="edition" className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Edition</label>
            <input type="number" id="edition" name="edition" placeholder="280" disabled={!!state.pdf.file} size="2" className="appearance-none uppercase block w-full bg-gray-200 text-gray-700 border border-red-500 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white" required />
          </div>
        </div>
        {pdfStatus !== 'success' && (
          <div>
            <button type="submit" disabled={!selectedFile || !!state.pdf.file || pdfStatus === 'deleting'} className={`${(buttonStatus[pdfStatus]?.styles) || 'bg-grey cursor-not-allowed'} font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase text-white m-3`}>
              {buttonStatus[pdfStatus]?.message || 'Upload magazine'}{buttonStatus[pdfStatus]?.animated && <AnimatedDots />}
            </button>
          </div>
        )}
      </form>
      {pdfStatus === 'success' && (
        <div className="bg-green rounded text-md text-white py-3 px-5 m-3">
          Magazine uploaded successfully!
          <br />
          Save the post to apply the changes.
        </div>
      )}
      {pdfStatus === 'uploadError' && (
        <div className="bg-red rounded text-md text-white py-3 px-5 m-3">
          The file could not be uploaded.
        </div>
      )}
      {pdfStatus === 'deleteError' && (
        <div className="bg-red rounded text-md text-white py-3 px-5 m-3">
          The file could not be deleted.
        </div>
      )}
    </div>
  );
};

PdfMagazineGallery.propTypes = {
  components: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handlerSetComponents: PropTypes.func.isRequired,
  activeLanguage: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired, // NOTE: id is used by the Drag and Drop to identify the component
};

export default PdfMagazineGallery;
